import { ChangeDetectorRef, Component, Input, OnChanges, OnInit, QueryList, SimpleChanges, ViewChildren } from '@angular/core';
import {
    AttributesValueType,
    CompanyAttributeDTO,
    CompanyProductSelectionAttributeWithOverrideInformation
} from '@iota/iota-management-portal-v4-client';
import { Store } from '@ngrx/store';
import { LoadIotaConfig } from 'src/app/store/actions/iota-config.actions';
import {
    filterIgnoredAttributes,
    iotaConfigAttributesByScope,
    iotaConfigLoaded,
    iotaConfigLoading,
    iotaConfigLocales
} from 'src/app/store/reducer/iota-config.reducer';
import { IotaConfigAttribute, IotaConfigAttributeScope } from '../../constants-and-enums/iota-config';
import { attributeCategoryLabel, sortAttributeSections } from '../../utils/attribute-category.util';
import {
    ATTRIBUTE_EXPORT_SEPARATOR,
    exportArrayToExcel,
    getAttributeFirstValue,
    getValueFromStore,
    jsonStringifyArrayAndConcatenate,
    waitForBooleanValue
} from '../../utils';
import { SimplifiedAttributeComponent } from './simplified-attribute/simplified-attribute.component';
import * as _ from 'lodash-es';
import { AttributeV2Component } from './attribute-v2/attribute-v2.component';
import {
    DialogImportAttributesComponent,
    ImportAttributesResult
} from '../../dialogs/dialog-import-attributes/dialog-import-attributes.component';
import { MatDialog } from '@iota/iota-ui-components';
import { isIotaAdmin } from 'src/app/store/reducer/user.reducer';
import { selectedLanguage } from 'src/app/store/reducer/language.reducer';
import { ProductCategoryType } from '@iota/iota-management-portal-v4-client';
import { ProductAttribute } from '../../constants-and-enums/attribute-types';
import { InhLanguage } from '../../constants-and-enums/language';
import { ToastService } from '../../services/toast.service';

export const VALIDATION_ERROR_MESSAGE = $localize`Some attributes are not valid and cannot be saved. Please correct them and try again.`;

export interface AttributeListResult {
    upsertedAttributes: CompanyAttributeDTO[];
    removedAttributeIds: string[];
}

export interface PreviousAndCurrentAttribute {
    previousAttribute: CompanyAttributeDTO | CompanyProductSelectionAttributeWithOverrideInformation;
    currentAttribute: CompanyAttributeDTO | CompanyProductSelectionAttributeWithOverrideInformation;
}

interface AttributeSection {
    key: string;
    label: string;
    groups: AttributeGroup[];
    numberOfAttributesWithThisParentId?: number;
}

interface AttributeGroup {
    previousAndCurrentAttributes: PreviousAndCurrentAttribute[];
    unusedLocales: string[];
    simpleAttribute: boolean;
    iotaConfigAttribute?: IotaConfigAttribute;
    addPossible?: boolean;
}

@Component({
    standalone: false,
    selector: 'app-attribute-list-v2',
    templateUrl: './attribute-list-v2.component.html'
})
export class AttributeListV2Component implements OnInit, OnChanges {
    @ViewChildren('simpleAttributeElement') simpleAttributeChildren: QueryList<SimplifiedAttributeComponent>;
    @ViewChildren('complexAttributeElement') complexAttributeChildren: QueryList<AttributeV2Component>;
    @Input() additionalInformationTemplate: any;
    @Input() attributes: CompanyAttributeDTO[] | CompanyProductSelectionAttributeWithOverrideInformation[];
    @Input() parentId: string;

    // When we have a list of attributes from different objects, we can mark the attributes that belong to this parent.
    // Example: In CompanyProductSelections, we show Product-attributes and (overriding) CPS-attributes, and we mark the CPS-attributes
    @Input() markIfParentIdIsThisParentId = false;
    @Input() overridesOtherAttributeTooltip: string;
    @Input() parentIdIsThisParentIdTooltip: string;

    @Input() showNumberOfAttributesWithThisParentIdBadge = false;
    @Input() numberOfAttributesWithThisParentIdBadgeSuffix: string;

    @Input() scope: IotaConfigAttributeScope;
    @Input() productCategory: ProductCategoryType;

    @Input() editMode: boolean;

    visibleAttributes: CompanyAttributeDTO[] = [];

    isIotaAdmin$ = this.store.select(isIotaAdmin);
    selectedLanguage$ = this.store.select(selectedLanguage);

    iotaConfigAttributes: IotaConfigAttribute[];
    locales: string[] = [];
    usedAttributeNames: string[] = [];

    otherAttributesKey = $localize`Other Attributes`;
    attributeSections: AttributeSection[] = [];
    numberOfUpsertedByCategory = new Map<string, number>();
    numberOfDeletedByCategory = new Map<string, number>();

    // Only for attributes in iota-config: Also show the real name (as it is stored in the DB)
    showRealAttributeName = false;
    trackByAttributeSection = (index: number, section: AttributeSection) => section.key;

    constructor(
        private store: Store,
        private cdr: ChangeDetectorRef,
        private dialog: MatDialog,
        private toastService: ToastService
    ) {}

    ngOnInit(): void {
        this.initAttributeList();
    }

    ngOnChanges(changes: SimpleChanges): void {
        const prevAttributes = changes?.attributes?.previousValue;
        const currentAttributes = changes?.attributes?.currentValue;
        const attributesChanged = !_.isEqual(prevAttributes, currentAttributes);

        const prevEditMode = changes?.editMode?.previousValue;
        const currentEditMode = changes?.editMode?.currentValue;
        const editModeChangedToFalse = prevEditMode === true && currentEditMode === false;

        const scopeChanged = changes?.scope && !changes.scope.firstChange;
        const productCategoryChanged = changes?.productCategory && !changes.productCategory.firstChange;

        if (attributesChanged || editModeChangedToFalse || scopeChanged || productCategoryChanged) {
            this.initAttributeList();
        }
    }

    async initAttributeList() {
        const configLoaded = await getValueFromStore(this.store, iotaConfigLoaded);
        if (!configLoaded) {
            this.store.dispatch(LoadIotaConfig());
            await waitForBooleanValue(this.store, iotaConfigLoading, false);
        }
        const language = await getValueFromStore(this.store, selectedLanguage);

        if (!this.attributes) return;

        this.visibleAttributes = this.attributes.filter((it) => filterIgnoredAttributes(this.scope, it.attributeName));

        const attributesByCategory = new Map<string, AttributeGroup[]>();
        this.numberOfUpsertedByCategory.clear();
        this.numberOfDeletedByCategory.clear();

        this.iotaConfigAttributes = await getValueFromStore(this.store, iotaConfigAttributesByScope(this.scope, this.productCategory));
        this.locales = await getValueFromStore(this.store, iotaConfigLocales);

        const allAttributes = this.iotaConfigAttributes
            .map((iotaConfigAttribute): AttributeGroup => {
                // Get all attributes with this name (overwritten attributes must be filtered out)
                const attributes = this.visibleAttributes
                    .filter((attr) => attr.attributeName === iotaConfigAttribute.name)
                    .filter((attr) => !this.isAttributeWithOverrideInformation(attr) || !attr.overwritten)
                    .map((attr) => ({ ...attr }));

                // Only string attributes (which don't have a list of possible values) can have multiple locales
                const addPossible = iotaConfigAttribute.type === 'STRING' && !iotaConfigAttribute?.possibleValues;

                const unusedLocales = this.getUnusedLocales(attributes);

                const previousAndCurrentAttributes: PreviousAndCurrentAttribute[] = [];
                if (attributes.length === 0) {
                    // We need these "empty" elements to show the attribute-component even if no attribute exists
                    previousAndCurrentAttributes.push({ previousAttribute: undefined, currentAttribute: undefined });
                } else {
                    previousAndCurrentAttributes.push(
                        ...attributes.map((attr) => ({
                            previousAttribute: attr,
                            currentAttribute: attr
                        }))
                    );
                }

                const customUnit = this.getCustomUnit(iotaConfigAttribute, language);

                return {
                    iotaConfigAttribute: { ...iotaConfigAttribute, customUnit },
                    previousAndCurrentAttributes: previousAndCurrentAttributes,
                    unusedLocales,
                    addPossible,
                    simpleAttribute: true
                };
            })
            .sort((a, b) => {
                // Show required attributes before not required ones
                if (a.iotaConfigAttribute?.required !== b.iotaConfigAttribute?.required) {
                    return (b.iotaConfigAttribute?.required ? 1 : 0) - (a.iotaConfigAttribute?.required ? 1 : 0);
                }

                // Show existing attributes before not existing ones
                if (!!a?.previousAndCurrentAttributes?.[0]?.currentAttribute !== !!b?.previousAndCurrentAttributes?.[0]?.currentAttribute) {
                    return (
                        (!!b?.previousAndCurrentAttributes?.[0]?.currentAttribute ? 1 : 0) -
                        (!!a?.previousAndCurrentAttributes?.[0]?.currentAttribute ? 1 : 0)
                    );
                }

                // Keep the original order
                return 0;
            });

        // Collect all attributes with the same category
        attributesByCategory.clear();
        allAttributes.forEach((attr) => {
            const category = attr.iotaConfigAttribute?.category;
            const attributes = attributesByCategory.get(category) ?? [];
            attributesByCategory.set(category, [...attributes, attr]);
            this.numberOfUpsertedByCategory.set(category, 0);
            this.numberOfDeletedByCategory.set(category, 0);
        });

        // All attribute ids where an entry in the iota-config exists
        const allAttributeIdsWithIotaConfig = allAttributes
            .map((group) => group?.previousAndCurrentAttributes?.map((attr) => attr?.currentAttribute?.attributeId))
            .flat()
            .filter((id) => !!id);

        // All elements where no entry in the iota-config exists are shown as "complex" attributes (overwritten attributes must be filtered out)
        const otherAttributes = this.visibleAttributes
            .filter((attr) => !allAttributeIdsWithIotaConfig.includes(attr.attributeId))
            .filter((attr) => !this.isAttributeWithOverrideInformation(attr) || !attr.overwritten)
            .map((attr) => ({ ...attr }));
        const attributesByName = new Map<string, CompanyAttributeDTO[]>();
        otherAttributes.forEach((attr) => {
            const attributesWithSameName = attributesByName.get(attr.attributeName) ?? [];
            attributesWithSameName.push(attr);
            attributesByName.set(attr.attributeName, attributesWithSameName);
        });
        const otherGroups: AttributeGroup[] = [...attributesByName.values()].map((attributes): AttributeGroup => {
            const unusedLocales = this.getUnusedLocales(attributes);
            const addPossible = attributes?.[0]?.valueType === AttributesValueType.STRING;

            return {
                previousAndCurrentAttributes: attributes.map((attr) => ({ previousAttribute: attr, currentAttribute: attr })),
                unusedLocales: unusedLocales,
                addPossible,
                simpleAttribute: false
            };
        });
        attributesByCategory.set(this.otherAttributesKey, otherGroups);
        this.numberOfUpsertedByCategory.set(this.otherAttributesKey, 0);
        this.numberOfDeletedByCategory.set(this.otherAttributesKey, 0);

        const sections = [...attributesByCategory].map(
            ([key, groups]): AttributeSection => ({
                key: key,
                label: attributeCategoryLabel(this.scope, key),
                groups,
                numberOfAttributesWithThisParentId: groups
                    ?.map(
                        (group) =>
                            group?.previousAndCurrentAttributes?.filter((attr) => attr?.currentAttribute?.parentSkId === this.parentId)
                                ?.length
                    )
                    ?.reduce((a, b) => a + b, 0)
            })
        );
        this.attributeSections = sortAttributeSections(sections, this.otherAttributesKey);
        this.usedAttributeNames = [...allAttributes.map((attr) => attr.iotaConfigAttribute.name), ...attributesByName.keys()];
    }

    addLocale(group: AttributeGroup) {
        if (group.previousAndCurrentAttributes?.[0]?.currentAttribute === undefined) {
            group.previousAndCurrentAttributes?.splice(0, 1);
        }

        const currentAttributes = group.previousAndCurrentAttributes.map((elem) => elem.currentAttribute);

        const unusedLocales = this.getUnusedLocales(currentAttributes).filter((locale) => !!locale);
        const nextUnusedLocale = unusedLocales?.[0];
        group.previousAndCurrentAttributes.push({
            previousAttribute: undefined,
            currentAttribute: {
                attributeName: group.previousAndCurrentAttributes?.[0]?.previousAttribute?.attributeName,
                valueType: AttributesValueType.STRING,
                locale: nextUnusedLocale
            }
        });

        // If all locales are used, disable add
        if (unusedLocales.length === 1) {
            group.addPossible = false;
        }
    }

    getUnusedLocales(attributes: CompanyAttributeDTO[]) {
        const usedLocales = attributes.map((attr) => attr.locale);
        const unusedLocales = this.locales?.filter((locale) => !usedLocales.includes(locale));

        const attributesWithoutLocale = attributes.some((attr) => attr.valueType === AttributesValueType.STRING && !attr.locale);
        if (attributesWithoutLocale) unusedLocales.unshift('');

        return unusedLocales;
    }

    addAttribute(key: string) {
        const attributeGroups = this.attributeSections?.find((section) => section?.key === key)?.groups ?? [];
        attributeGroups.unshift({
            previousAndCurrentAttributes: [{ previousAttribute: undefined, currentAttribute: undefined }],
            simpleAttribute: false,
            unusedLocales: this.locales
        });
    }

    removeAttribute(group: AttributeGroup, attribute: PreviousAndCurrentAttribute) {
        const index = group.previousAndCurrentAttributes?.indexOf(attribute);
        if (index !== -1) {
            group.previousAndCurrentAttributes?.splice(index, 1);
            if (group.simpleAttribute && group.previousAndCurrentAttributes.length === 0) {
                group.previousAndCurrentAttributes.push({ currentAttribute: undefined, previousAttribute: undefined });
            }
            group.addPossible = true;
        }
    }

    hasValidationErrors(): boolean {
        return this.simpleAttributeChildren?.toArray().some((attribute) => attribute.isInvalid && !attribute.markedForDelete) ?? false;
    }

    showValidationErrorNotification(): void {
        this.toastService.showError(VALIDATION_ERROR_MESSAGE);
    }

    getAttributeListResult(): AttributeListResult {
        const simpleChildren = this.simpleAttributeChildren.toArray();
        const simpleUpsertedAttributes = simpleChildren.map((attr) => attr.getUpsertedAttribute()).filter((attr) => !!attr);
        const simpleRemovedAttributeIds = simpleChildren.map((attr) => attr.getDeletedAttributeId()).filter((id) => !!id);

        const complexChildren = this.complexAttributeChildren.toArray();
        const complexUpsertedAttributes = complexChildren.map((attr) => attr.getUpsertedAttribute()).filter((attr) => !!attr);
        const complexRemovedAttributeIds = complexChildren.map((attr) => attr.getDeletedAttributeId()).filter((id) => !!id);

        const combinedUpsertedAttributes = [...simpleUpsertedAttributes, ...complexUpsertedAttributes].map((attr) => ({
            ...attr,
            parentSkId: attr.parentSkId ?? this.parentId
        }));

        return {
            upsertedAttributes: combinedUpsertedAttributes,
            removedAttributeIds: [...simpleRemovedAttributeIds, ...complexRemovedAttributeIds]
        };
    }

    modifyDeleteNumber(key: string, modifyValue: number) {
        const value = this.numberOfDeletedByCategory.get(key) ?? 0;
        this.numberOfDeletedByCategory.set(key, value + modifyValue);
        this.cdr.detectChanges();
    }

    modifyUpsertNumber(key: string, modifyValue: number) {
        const value = this.numberOfUpsertedByCategory.get(key) ?? 0;
        this.numberOfUpsertedByCategory.set(key, value + modifyValue);
        this.cdr.detectChanges();
    }

    exportAttributes(): void {
        const attributeList = this.formatExportedAttributes();
        exportArrayToExcel(attributeList);
    }

    private formatExportedAttributes(): any[] {
        return this.attributes
            .filter((it) => filterIgnoredAttributes(this.scope, it.attributeName))
            .map((obj) => {
                const exportObj = {};

                // it's better to only copy wanted properties, so new ones don't automatically get exported
                exportObj[`attributeName`] = obj.attributeName;
                exportObj[`valueType`] = obj.valueType;
                exportObj[`temporary`] = obj.temporary ? obj.temporary : false;
                exportObj[`booleanValue`] = jsonStringifyArrayAndConcatenate(obj.booleanValue, ATTRIBUTE_EXPORT_SEPARATOR);
                exportObj[`dateTimeValue`] = jsonStringifyArrayAndConcatenate(
                    obj.dateTimeValue.map((date) => new Date(date)),
                    ATTRIBUTE_EXPORT_SEPARATOR
                );
                exportObj[`dateValue`] = jsonStringifyArrayAndConcatenate(
                    obj.dateValue.map((date) => new Date(date)),
                    ATTRIBUTE_EXPORT_SEPARATOR
                );
                exportObj[`intValue`] = jsonStringifyArrayAndConcatenate(obj.intValue, ATTRIBUTE_EXPORT_SEPARATOR);
                exportObj[`numValue`] = jsonStringifyArrayAndConcatenate(obj.numValue, ATTRIBUTE_EXPORT_SEPARATOR);
                exportObj[`strValue`] = jsonStringifyArrayAndConcatenate(obj.strValue, ATTRIBUTE_EXPORT_SEPARATOR);
                exportObj[`uuidValue`] = jsonStringifyArrayAndConcatenate(obj.uuidValue, ATTRIBUTE_EXPORT_SEPARATOR);
                exportObj[`locale`] = obj.locale;

                return exportObj;
            });
    }

    importAttributes(): void {
        this.editMode = true;
        this.dialog
            .open(DialogImportAttributesComponent)
            .afterClosed()
            .subscribe((res: ImportAttributesResult) => {
                if (!res?.attributes) return;

                const importedAttributes = [...res.attributes].filter((it) => filterIgnoredAttributes(this.scope, it.attributeName));

                const allGroups = this.attributeSections?.map((section) => section?.groups)?.flat();

                importedAttributes.forEach((attr) => {
                    const group = allGroups.find(
                        (group) =>
                            group?.iotaConfigAttribute?.name === attr.attributeName ||
                            group?.previousAndCurrentAttributes?.[0]?.currentAttribute?.attributeName === attr.attributeName
                    );

                    // If attribute with this name doesn't exist, create new group for it
                    if (!group) {
                        const otherAttributeGroups =
                            this.attributeSections?.find((section) => section?.key === this.otherAttributesKey)?.groups ?? [];
                        otherAttributeGroups.unshift({
                            previousAndCurrentAttributes: [{ previousAttribute: undefined, currentAttribute: attr }],
                            simpleAttribute: false,
                            unusedLocales: this.locales
                        });
                        return;
                    }

                    // If the group exists, but the "Add"-button was not clicked yet
                    if (group?.previousAndCurrentAttributes?.[0] === undefined) {
                        group.previousAndCurrentAttributes = [{ previousAttribute: undefined, currentAttribute: attr }];
                        return;
                    }

                    // If only one attribute exists and not string attribute
                    if (attr.valueType !== AttributesValueType.STRING && group.previousAndCurrentAttributes.length === 1) {
                        group.previousAndCurrentAttributes[0].currentAttribute = attr;
                    }

                    // If string attribute, check if locale exists. If yes, overwrite the attribute, else create a new one
                    const existingAttributeWithThisLocale = group.previousAndCurrentAttributes?.find(
                        (prevAndCurrentAttribute) => prevAndCurrentAttribute.currentAttribute?.locale === attr.locale
                    );
                    if (existingAttributeWithThisLocale) {
                        existingAttributeWithThisLocale.currentAttribute = attr;
                    } else {
                        group.previousAndCurrentAttributes.push({
                            previousAttribute: undefined,
                            currentAttribute: attr
                        });
                    }
                });
            });
    }

    getCustomUnit(iotaConfigAttribute: IotaConfigAttribute, language: InhLanguage) {
        const attributeName = iotaConfigAttribute?.name;

        // The unit for PRODUCT_CANCELLATION_DATE_START and PRODUCT_CANCELLATION_DATE_END
        // is defined by PRODUCT_CANCELLATION_DATE_INTERVAL
        if (
            attributeName === ProductAttribute.PRODUCT_CANCELLATION_DATE_START ||
            attributeName === ProductAttribute.PRODUCT_CANCELLATION_DATE_END
        ) {
            const intervalAttributeValue = getAttributeFirstValue(
                { attributes: this.visibleAttributes },
                ProductAttribute.PRODUCT_CANCELLATION_DATE_INTERVAL
            );
            const intervalIotaConfigAttribute = this.iotaConfigAttributes?.find(
                (attr) => attr.name === ProductAttribute.PRODUCT_CANCELLATION_DATE_INTERVAL
            );

            return intervalIotaConfigAttribute?.possibleValuesLabels?.[intervalAttributeValue]?.[language?.countryCode];
        }

        // The unit for MINIMUM_TERM_AMOUNT is defined by MINIMUM_TERM_PERIOD
        if (attributeName === ProductAttribute.MINIMUM_TERM_AMOUNT) {
            const intervalAttributeValue = getAttributeFirstValue(
                { attributes: this.visibleAttributes },
                ProductAttribute.MINIMUM_TERM_PERIOD
            );
            const intervalIotaConfigAttribute = this.iotaConfigAttributes?.find(
                (attr) => attr.name === ProductAttribute.MINIMUM_TERM_PERIOD
            );

            return intervalIotaConfigAttribute?.possibleValuesLabels?.[intervalAttributeValue]?.[language?.countryCode];
        }

        // The unit for BUDGET_RENEWAL_PERIOD_AMOUNT is defined by BUDGET_RENEWAL_PERIOD
        if (attributeName === ProductAttribute.BUDGET_RENEWAL_PERIOD_AMOUNT) {
            const intervalAttributeValue = getAttributeFirstValue(
                { attributes: this.visibleAttributes },
                ProductAttribute.BUDGET_RENEWAL_PERIOD
            );
            const intervalIotaConfigAttribute = this.iotaConfigAttributes?.find(
                (attr) => attr.name === ProductAttribute.BUDGET_RENEWAL_PERIOD
            );

            return intervalIotaConfigAttribute?.possibleValuesLabels?.[intervalAttributeValue]?.[language?.countryCode];
        }
        return undefined;
    }

    isAttributeWithOverrideInformation(
        attribute: CompanyAttributeDTO | CompanyProductSelectionAttributeWithOverrideInformation
    ): attribute is CompanyProductSelectionAttributeWithOverrideInformation {
        if (!attribute) return false;

        return 'overwritten' in attribute;
    }
}
